import { useEffect, useRef } from 'react';

import { useUserInfoStore } from '~/store';

import { BUTTON_OPTION, CONFIG, SCRIPT_SOURCE } from '~/constants';

type TwcInitOptions = {
  brandKey: string;
  channelType: string;
  scenarioId: string;
  shipperName: string;
  buttonOption: {
    showLauncher: boolean;
    zIndex: number;
    bottom: number;
    right: number;
  };
};

const getOption = (
  config: typeof CONFIG.FBK | (typeof CONFIG)['3PL_NFA'],
  ownerName: string,
  showLauncher: boolean
): TwcInitOptions => {
  return {
    brandKey: config.brandKey,
    channelType: 'scenario',
    scenarioId: config.scenarioId,
    shipperName: ownerName,
    buttonOption: { ...BUTTON_OPTION, showLauncher },
  };
};

const injectScript = async (src: string, scriptLoadedRef: React.MutableRefObject<boolean>): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (scriptLoadedRef.current) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

const useTwcChatbot = () => {
  const scriptLoadedRef = useRef(false);

  const {
    userInfo: { loginId, ownerName },
    hasPermission,
  } = useUserInfoStore();
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  useEffect(() => {
    const isLoggedIn = Boolean(loginId);
    const config = hasPermissionFBK ? CONFIG.FBK : CONFIG['3PL_NFA'];

    const initTWC = () => {
      if (!window.Twc) return;
      try {
        if ('Chat' in window.Twc) {
          window.Twc.Chat.init('init', getOption(config, ownerName, true));
        } else {
          window.Twc('init', getOption(config, ownerName, true));
        }
      } catch (error) {
        console.error('[TWC Chatbot] 초기화 중 에러:', error);
      }
    };

    const destroyTWC = () => {
      if (!window.Twc) return;
      try {
        if ('Chat' in window.Twc) {
          window.Twc.Chat.init('init', getOption(config, ownerName, false));
          window.Twc.Chat.conclude?.();
        } else {
          window.Twc('init', getOption(config, ownerName, false));
        }
      } catch (error) {
        console.error('[TWC Chatbot] 정리 중 에러:', error);
      }
    };

    if (isLoggedIn) {
      injectScript(SCRIPT_SOURCE, scriptLoadedRef)
        .then(initTWC)
        .catch((error) => {
          console.error('[TWC Chatbot] 스크립트 로드 실패:', error);
        });
    } else {
      destroyTWC();
    }

    return () => {
      destroyTWC();
    };
  }, [loginId, ownerName, hasPermissionFBK]);
};

export default useTwcChatbot;
